import type { Customer } from '@/features/intelligence-dashboard/customer.schema';

import { INSIGHT_ORDER, INSIGHT_TYPES } from '../campaigns-config';
import { decayClass, decayOpacity } from '../customer-derived';

interface SignalDotsProps {
  insights: Customer['insights'];
}

/**
 * The four-dot insight indicator (SCRUM-206), one dot per insight type in
 * INSIGHT_ORDER. Active dots take their insight's semantic color with an
 * age-based decay opacity (fresh 1.0 / aging 0.55 / stale 0.28 + desaturate);
 * inactive dots are a hollow neutral swatch.
 */
export function SignalDots({ insights }: SignalDotsProps) {
  return (
    <div className="flex items-center gap-1">
      {INSIGHT_ORDER.map((k) => {
        const flag = insights[k];
        const active = Boolean(flag?.active);
        const meta = INSIGHT_TYPES[k];
        const title = active
          ? `${meta.label} — triggered ${String(flag?.age ?? 0)}d ago`
          : `${meta.label} — not active`;

        if (!active) {
          return (
            <span
              key={k}
              title={title}
              className="border-border-1 bg-bg-3 inline-block h-[9px] w-[9px] rounded-full border"
            />
          );
        }

        const decay = decayClass(flag?.age ?? 0);
        const color = `var(${meta.colorVar})`;
        return (
          <span
            key={k}
            title={title}
            className="inline-block h-[9px] w-[9px] rounded-full border"
            style={{
              backgroundColor: color,
              borderColor: color,
              opacity: decayOpacity(decay),
              filter: decay === 'stale' ? 'saturate(0.4)' : undefined,
            }}
          />
        );
      })}
    </div>
  );
}
